import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subject, BehaviorSubject } from 'rxjs';
import { tap, shareReplay, takeUntil } from 'rxjs/operators';
import { LandaService } from './landa.service';

interface CacheItem<T> {
    data: T;
    timestamp: number;
}

interface ClientStatusData {
    jumlahKaryawanAllPerusahaan: number;
    max_karyawan: number;
    listKaryawanModal: any[];
    is_opsi_akun_gratis: any;
    tipe_setting_invoice: any;
    is_session_admin_invoice: boolean;
    is_setting_invoice_terisi: boolean;
    setting_invoice_branch: any;
    is_invoice_perusahaan_terbayar: boolean;
    is_paket_custom: boolean;
    isset_invoice_pending: boolean;
    sisa_masa_aktif: any;
    sisa_masa_aktif_real: any;
    reminder_masa_aktif: any;
    masa_trial: any;
    is_invoice_terbayar: any;
    jumlah_perusahaan: any;
    max_perusahaan: any;
    rawResponse: any; // Store full response for complex logic
}

@Injectable({
    providedIn: 'root'
})
export class ClientStatusService implements OnDestroy {
    // Cache duration in milliseconds (5 minutes default)
    private readonly CACHE_DURATION = 5 * 60 * 1000;

    // Cache storage
    private cache: { [key: string]: CacheItem<any> } = {};

    // Visibility change handling
    private visibilityChangeHandler: () => void;
    private lastVisibilityCheck: number = 0;

    // Observable subjects for reactive updates
    private statusClient$ = new BehaviorSubject<ClientStatusData | null>(null);
    private destroy$ = new Subject<void>();

    // Pending requests to prevent duplicate calls
    private pendingRequests: { [key: string]: Observable<any> } = {};

    constructor(private landaService: LandaService) {
        this.setupVisibilityListener();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.removeVisibilityListener();
    }

    /**
     * Initialize the service - call this once when app starts
     */
    init(): void {
        // This is called when horizontaltopbar initializes
        // Visibility listener is already set up in constructor
    }

    /**
     * Setup visibility change listener for sleep mode handling
     */
    private setupVisibilityListener(): void {
        this.visibilityChangeHandler = () => {
            if (document.visibilityState === 'visible') {
                const now = Date.now();
                // Check if we need to refresh (if more than cache duration passed)
                if (now - this.lastVisibilityCheck > this.CACHE_DURATION) {
                    this.invalidateAllCache();
                }
                this.lastVisibilityCheck = now;
            }
        };

        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        this.lastVisibilityCheck = Date.now();
    }

    /**
     * Remove visibility listener
     */
    private removeVisibilityListener(): void {
        if (this.visibilityChangeHandler) {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        }
    }

    /**
     * Check if cache is valid for a given key
     */
    private isCacheValid(key: string): boolean {
        const cached = this.cache[key];
        if (!cached) return false;

        const now = Date.now();
        return (now - cached.timestamp) < this.CACHE_DURATION;
    }

    /**
     * Get cached data or null if expired/missing
     */
    private getCachedData<T>(key: string): T | null {
        if (this.isCacheValid(key)) {
            return this.cache[key].data;
        }
        return null;
    }

    /**
     * Store data in cache
     */
    private setCachedData<T>(key: string, data: T): void {
        this.cache[key] = {
            data,
            timestamp: Date.now()
        };
    }

    /**
     * Invalidate all cache
     */
    invalidateAllCache(): void {
        this.cache = {};
    }

    /**
     * Invalidate specific cache key
     */
    invalidateCache(key: string): void {
        delete this.cache[key];
    }

    /**
     * Force refresh all data
     */
    forceRefreshAll(): void {
        this.invalidateAllCache();
    }

    // ==================== CACHED API METHODS ====================

    /**
     * Get client status (cached)
     * Endpoint: /appclient/getStatusClient
     */
    getStatusClient(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'statusClient';

        // Return cached if valid and not forcing refresh
        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        // Return pending request if one exists
        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        // Make new request
        this.pendingRequests[cacheKey] = this.landaService.DataGet('/appclient/getStatusClient', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];

                // Update BehaviorSubject
                if (res.data && res.data.list && res.data.list.data) {
                    const data = res.data.list.data;
                    this.statusClient$.next({
                        jumlahKaryawanAllPerusahaan: data.jumlah_all_karyawan_perusahaan,
                        max_karyawan: data.max_karyawan,
                        listKaryawanModal: res.data.karyawan,
                        is_opsi_akun_gratis: data.is_opsi_akun_gratis,
                        tipe_setting_invoice: res.data.tipe_setting_invoice,
                        is_session_admin_invoice: res.data.is_session_admin_invoice,
                        is_setting_invoice_terisi: res.data.is_setting_invoice_terisi,
                        setting_invoice_branch: res.data.setting_invoice_branch,
                        is_invoice_perusahaan_terbayar: res.data.is_invoice_terbayar,
                        is_paket_custom: data.is_custom,
                        isset_invoice_pending: res.data.isset_invoice_pending,
                        sisa_masa_aktif: data.masa_trial,
                        sisa_masa_aktif_real: data.sisa_masa_aktif_real,
                        reminder_masa_aktif: data.reminder_masa_aktif,
                        masa_trial: data.masa_trial,
                        is_invoice_terbayar: data.is_invoice_terbayar,
                        jumlah_perusahaan: data.jumlah_perusahaan,
                        max_perusahaan: data.max_perusahaan,
                        rawResponse: res
                    });
                }
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get akses karyawan (cached)
     * Endpoint: /appperusahaan/getAksesKaryawan
     */
    getAksesKaryawan(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'aksesKaryawan';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        const params = { id: '', kode: '', nama: '', sumber: 1 };
        this.pendingRequests[cacheKey] = this.landaService.DataGet('/appperusahaan/getAksesKaryawan', params).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get notifikasi pengumuman (cached)
     * Endpoint: /apppengumuman/getNotifikasiPengumuman
     */
    getNotifikasiPengumuman(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'notifikasiPengumuman';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/apppengumuman/getNotifikasiPengumuman', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get menu client dashboard (cached)
     * Endpoint: /appclient/getMenuClientDashboard
     */
    getMenuClientDashboard(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'menuClientDashboard';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/appclient/getMenuClientDashboard', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get logo navbar (cached - longer duration)
     * Endpoint: /site/getLogoNavbar
     */
    getLogoNavbar(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'logoNavbar';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/site/getLogoNavbar', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get paket klien (cached - longer duration)
     * Endpoint: /site/getPaketKlien
     */
    getPaketKlien(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'paketKlien';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/site/getPaketKlien', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get cek versi (cached)
     * Endpoint: /backofficepengaturan/cekversi
     */
    getCekVersi(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'cekVersi';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/backofficepengaturan/cekversi', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    /**
     * Get setting by session (cached)
     * Endpoint: /appperusahaan/getSettingBySession
     */
    getSettingBySession(forceRefresh: boolean = false): Observable<any> {
        const cacheKey = 'settingBySession';

        if (!forceRefresh && this.isCacheValid(cacheKey)) {
            return of(this.cache[cacheKey].data);
        }

        if (this.pendingRequests[cacheKey]) {
            return this.pendingRequests[cacheKey];
        }

        this.pendingRequests[cacheKey] = this.landaService.DataGet('/appperusahaan/getSettingBySession', {}).pipe(
            tap((res: any) => {
                this.setCachedData(cacheKey, res);
                delete this.pendingRequests[cacheKey];
            }),
            shareReplay(1)
        );

        return this.pendingRequests[cacheKey];
    }

    // ==================== NON-CACHED API METHODS ====================
    // These are POST requests or ones that should always be fresh

    /**
     * Set fitur - always fresh (POST request)
     * Endpoint: /appfitur/setFitur
     */
    setFitur(akses: any): Observable<any> {
        return this.landaService.DataPost('/appfitur/setFitur', akses);
    }

    // ==================== GETTERS FOR CACHED DATA ====================

    /**
     * Get status client observable for reactive components
     */
    getStatusClientObservable(): Observable<ClientStatusData | null> {
        return this.statusClient$.asObservable();
    }

    /**
     * Get current cached status client data synchronously
     */
    getCurrentStatusClient(): ClientStatusData | null {
        return this.statusClient$.getValue();
    }
}
